import { useEffect, useRef, type ReactNode } from "react";

/**
 * HeroReveal
 * - AFTER image = permanent base (bottom).
 * - BEFORE image = top layer, radial-masked so only a local circle around
 *   the pointer is visible.
 * - children render ABOVE both images (gradient overlay, headline, CTAs).
 *
 * Pointer tracking is attached to the section root so events over the
 * gradient/text/CTA still update the reveal. CTAs remain clickable because
 * only images use pointer-events: none; children keep default events.
 *
 * Enable/disable is decided per real PointerEvent.pointerType, not matchMedia,
 * so preview iframes and hybrid devices behave correctly. Touch → disabled.
 */
export default function HeroReveal({
  afterSrc,
  beforeSrc,
  afterAlt,
  beforeAlt,
  afterClassName,
  className,
  style,
  children,
}: {
  afterSrc: string;
  beforeSrc: string;
  afterAlt: string;
  beforeAlt: string;
  afterClassName?: string;
  className?: string;
  style?: React.CSSProperties;
  children?: ReactNode;
}) {
  const rootRef = useRef<HTMLElement | null>(null);
  const beforeRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const root = rootRef.current;
    const before = beforeRef.current;
    if (!root || !before) return;

    let rectLeft = 0;
    let rectTop = 0;
    let rectW = 0;
    let rectH = 0;
    const measure = () => {
      const r = root.getBoundingClientRect();
      rectLeft = r.left;
      rectTop = r.top;
      rectW = r.width;
      rectH = r.height;
    };
    measure();

    let tx = rectW / 2;
    let ty = rectH / 2;
    let vx = tx;
    let vy = ty;
    let ts = 0; // target strength 0..1
    let vs = 0; // visible strength

    // Initial CSS vars so first paint is stable (radius 0 = nothing visible).
    before.style.setProperty("--rx", `${vx}px`);
    before.style.setProperty("--ry", `${vy}px`);
    before.style.setProperty("--rs", "0");

    let raf = 0;
    let running = true;
    let last = performance.now();
    const tick = (now: number) => {
      if (!running) return;
      const dt = Math.min(64, now - last);
      last = now;
      const aPos = 1 - Math.exp(-dt / 140);
      const aStr = 1 - Math.exp(-dt / 180);
      vx += (tx - vx) * aPos;
      vy += (ty - vy) * aPos;
      vs += (ts - vs) * aStr;
      before.style.setProperty("--rx", `${vx.toFixed(2)}px`);
      before.style.setProperty("--ry", `${vy.toFixed(2)}px`);
      before.style.setProperty("--rs", vs.toFixed(3));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    const handleMove = (e: PointerEvent) => {
      if (e.pointerType === "touch") return;
      tx = e.clientX - rectLeft;
      ty = e.clientY - rectTop;
    };
    const handleEnter = (e: PointerEvent) => {
      if (e.pointerType === "touch") return;
      measure();
      tx = e.clientX - rectLeft;
      ty = e.clientY - rectTop;
      ts = 1;
    };
    const handleLeave = (e: PointerEvent) => {
      if (e.pointerType === "touch") return;
      ts = 0;
    };
    const onResize = () => measure();
    const onScroll = () => measure();

    root.addEventListener("pointerenter", handleEnter);
    root.addEventListener("pointermove", handleMove, { passive: true });
    root.addEventListener("pointerleave", handleLeave);
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      root.removeEventListener("pointerenter", handleEnter);
      root.removeEventListener("pointermove", handleMove);
      root.removeEventListener("pointerleave", handleLeave);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  // Mask on BEFORE: opaque center = visible; transparent outside = hidden.
  // Radius scales with --rs (0 → 1). Solid core to 220px, feather to 300px.
  const maskImage =
    "radial-gradient(circle calc(300px * var(--rs, 0)) at var(--rx, 50%) var(--ry, 50%), rgba(0,0,0,1) 0px, rgba(0,0,0,1) calc(220px * var(--rs, 0)), rgba(0,0,0,0) 100%)";

  const beforeMaskStyle: React.CSSProperties = {
    WebkitMaskImage: maskImage,
    maskImage,
    WebkitMaskRepeat: "no-repeat",
    maskRepeat: "no-repeat",
    WebkitMaskSize: "100% 100%",
    maskSize: "100% 100%",
    willChange: "mask-image, -webkit-mask-image",
    pointerEvents: "none",
  };

  return (
    <section
      ref={(el) => { rootRef.current = el; }}
      className={`relative w-full overflow-hidden ${className ?? ""}`}
      style={style}
    >
      {/* AFTER — permanent base */}
      <img
        src={afterSrc}
        alt={afterAlt}
        draggable={false}
        className={`absolute inset-0 w-full h-full object-cover object-center select-none ${afterClassName ?? ""}`}
        style={{ pointerEvents: "none", zIndex: 1 }}
      />
      {/* BEFORE — masked overlay */}
      <img
        ref={beforeRef}
        src={beforeSrc}
        alt={beforeAlt}
        draggable={false}
        className="absolute inset-0 w-full h-full object-cover object-center select-none"
        style={{ ...beforeMaskStyle, zIndex: 2 }}
      />
      {/* Children (gradient overlay + headline + CTAs) sit above both images */}
      <div className="absolute inset-0" style={{ zIndex: 3 }}>
        {children}
      </div>
    </section>
  );
}